import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';

import { geocode } from '../../utils';
import MapContext from './MapContext';

const Markers = ({
  devices = [],
  markerClusterIcon,
  markerCluster,
  defaultLocation,
  onClick,
  onDblclick,
  onMouseup,
  onMousedown,
  onMouseover,
  onMouseout,
  onMarkerDrawn,
  onMarkerReDrawn,
  onMarkerRemoved
}) => {
  const map = useContext(MapContext);
  const markersRef = useRef([]);
  const unitsCurrentlyBeingAddedToMapRef = useRef([]);
  const boundaryRef = useRef(null);
  const markerClusterRef = useRef(null);
  
  // 1. CLUSTER RENDERER
  const renderer = {
    render: ({ count, position }) => {
      const container = document.createElement('div');
      container.style.position = 'relative';
      container.style.width = '28px';
      container.style.height = '25px';
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      container.style.justifyContent = 'center';

      const img = document.createElement('img');
      img.src = markerClusterIcon;
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.position = 'absolute';
      img.style.top = '0';
      img.style.left = '0';
      
      const label = document.createElement('span');
      label.innerText = String(count);
      label.style.color = 'white';
      label.style.fontSize = '11px';
      label.style.fontWeight = 'bold';
      label.style.zIndex = '2';
      label.style.position = 'relative';

      container.appendChild(img);
      container.appendChild(label);

      return new window.google.maps.marker.AdvancedMarkerElement({
        position,
        content: container,
        zIndex: Number(window.google.maps.Marker?.MAX_ZINDEX || 1000000) + count,
      });
    }
  };

  useEffect(() => {
    if (map && markerCluster) {
      try {
        markerClusterRef.current = new markerCluster({
          map,
          markers: [],
          renderer,
          maxZoom: 20,
          gridSize: 30
        });
      } catch (error) {
        console.error("Error initializing MarkerClusterer:", error);
      }
    }
  }, [map, markerCluster]);

  useEffect(() => {
    if (!map || !markerClusterRef.current) return;

    const updateMarkers = async () => {
      removeExtraMarkers();

      for (let device of devices) {
        if (!isUnitCurrentlyBeingAddedToMap(device.UnitNumber)) {
          unitsCurrentlyBeingAddedToMapRef.current.push(device.UnitNumber);

          let markerObj = getMarkerByUnitNumber(device.UnitNumber);
          
          const currentIconUrl = markerObj?.device?.icon;
          
          if (markerObj) {
            if (currentIconUrl && device.icon && currentIconUrl !== device.icon) {
              markerClusterRef.current.clearMarkers();
              await reDraw(markerObj, device);
              markerClusterRef.current.addMarkers(markersRef.current);
            }
          } else {
            await draw(device);
            markerClusterRef.current.clearMarkers();
            markerClusterRef.current.addMarkers(markersRef.current);
          }

          removeUnitCurrentlyBeingAddedToMap(device.UnitNumber);
        }
      }
    };

    updateMarkers();
  }, [devices, map]);

  // 2. FIXED: INCREASED MARKER SIZE
  const buildMarkerContent = (iconUrl) => {
    const iconImg = document.createElement('img');
    iconImg.src = iconUrl;
    
    // Changed from 14px to 28px (matching the original anchor point logic)
    // You can increase this to 32px or 40px if you want them even larger
    iconImg.style.width = '90px'; 
    iconImg.style.height = '90px';
    
    // Ensures images don't stretch weirdly if they aren't square
    iconImg.style.objectFit = 'contain'; 
    
    return iconImg;
  };

  const createMarker = async (device) => {
    try {
      let latLng = { ...device.position };

      if (!latLng.lng) {
        try {
          let results = await geocode(device.address);
          latLng = { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() };
        } catch (err) {
          latLng = { lat: -37.840935, lng: 144.946457 }; 
          if (defaultLocation && defaultLocation.lng && defaultLocation.lat) {
            latLng = defaultLocation;
          }
        }
      }

      const markerContent = buildMarkerContent(device.icon);

      let marker = new window.google.maps.marker.AdvancedMarkerElement({
        map: null,
        position: latLng,
        content: markerContent,
        title: device.name || '' 
      });

      marker.device = device;

      // Event Listeners
      if (onClick) {
        marker.addListener('click', () => onClick(marker));
      }

      if (onDblclick) {
         markerContent.addEventListener('dblclick', (e) => {
             e.stopPropagation(); 
             onDblclick(marker);
         });
      }

      if (onMouseup) markerContent.addEventListener('mouseup', () => onMouseup(marker));
      if (onMousedown) markerContent.addEventListener('mousedown', () => onMousedown(marker));
      if (onMouseover) markerContent.addEventListener('mouseover', () => onMouseover(marker));
      if (onMouseout) markerContent.addEventListener('mouseout', () => onMouseout(marker));

      return marker;
    } catch (err) {
      console.error('Error creating marker:', err);
      return null;
    }
  };

  const draw = async (device) => {
    let markerObj = await createMarker(device);
    if (!markerObj) return;

    markersRef.current.push(markerObj);

    if (!boundaryRef.current) {
      boundaryRef.current = new window.google.maps.LatLngBounds();
    }
    boundaryRef.current.extend(markerObj.position);
    map.fitBounds(boundaryRef.current);
    adjustMapZooming();

    if (onMarkerDrawn) onMarkerDrawn(markerObj);
  };

  const reDraw = async (marker, device) => {
    removeMarkerFromMap(marker);
    removeMarker(marker);

    let newMarker = await createMarker(device);
    if (!newMarker) return;

    replaceMarker(newMarker);
    if (onMarkerReDrawn) onMarkerReDrawn(newMarker);
  };

  const isUnitCurrentlyBeingAddedToMap = (unitNumber) => {
    return unitsCurrentlyBeingAddedToMapRef.current.includes(unitNumber);
  };

  const removeUnitCurrentlyBeingAddedToMap = (unitNumber) => {
    unitsCurrentlyBeingAddedToMapRef.current = unitsCurrentlyBeingAddedToMapRef.current.filter(
      unitNo => unitNo !== unitNumber
    );
  };

  const removeMarkerFromMap = async (markerObj) => {
    if (!markerObj) return;
    markerObj.map = null; 
  };

  const adjustMapZooming = () => {
    var zoom = map.getZoom();
    map.setZoom(zoom > 12 ? 12 : zoom);
  };

  const removeExtraMarkers = () => {
    let delMarkers = markersRef.current.filter(
      markerObj => devices.filter(device => device.UnitNumber === markerObj.device.UnitNumber).length === 0
    );

    delMarkers.forEach(objMarker => {
      objMarker.map = null;
    });

    markersRef.current = markersRef.current.filter(
      markerObj => devices.some(device => device.UnitNumber === markerObj.device.UnitNumber)
    );
    
    if(markerClusterRef.current) {
        markerClusterRef.current.clearMarkers();
        markerClusterRef.current.addMarkers(markersRef.current);
    }

    if (delMarkers.length > 0) {
      boundaryRef.current = new window.google.maps.LatLngBounds();
      markersRef.current.forEach(marker => {
        boundaryRef.current.extend(marker.position);
        if (onMarkerRemoved) onMarkerRemoved(marker);
      });
      
      if (boundaryRef.current.isEmpty() && defaultLocation?.lat) {
         boundaryRef.current.extend(defaultLocation);
      }
      
      if(!boundaryRef.current.isEmpty()){
        map.fitBounds(boundaryRef.current);
        adjustMapZooming();
      }
    }
  };

  const getMarkerByUnitNumber = (unitNumber) => {
    return markersRef.current.find(markerObj => markerObj.device.UnitNumber === unitNumber) || null;
  };

  const replaceMarker = (newMarker) => {
    if (!newMarker || !newMarker.device) return;
    markersRef.current = markersRef.current.filter(marker => marker.device.UnitNumber !== newMarker.device.UnitNumber);
    markersRef.current.push(newMarker);
  };

  const removeMarker = (markerObj) => {
    if (!markerObj || !markerObj.device) return;
    markersRef.current = markersRef.current.filter(marker => marker.device.UnitNumber !== markerObj.device.UnitNumber);
  };

  return null;
};

Markers.propTypes = {
  devices: PropTypes.array,
  markerClusterIcon: PropTypes.string,
  markerCluster: PropTypes.func,
  defaultLocation: PropTypes.object,
  onClick: PropTypes.func,
  onDblclick: PropTypes.func,
  onMouseup: PropTypes.func,
  onMousedown: PropTypes.func,
  onMouseover: PropTypes.func,
  onMouseout: PropTypes.func,
  onMarkerDrawn: PropTypes.func,
  onMarkerReDrawn: PropTypes.func,
  onMarkerRemoved: PropTypes.func
};

export default Markers;